import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export type AutoConnectorRole = "ota" | "supplier" | "dmc";

export type AutoConnectorState = {
  sessionId: string | null;
  currentPhase: number;
  role: AutoConnectorRole | null;
  supplierName: string;
  techEmail: string;
  hasAgreement: "yes" | "no" | null;
  uploadMode: "file" | "url";
  fileName: string | null;
  docUrl: string;
  crawlDepth: number;
  status: string | null;
  questionnaireDraft: Record<string, string>;
  submittedAt: number | null;
  /**
   * Quando true, l'utente è entrato via shortcut "Già integrati":
   * - currentPhase è bloccato sulla schermata finale
   * - ProgressRail non permette di andare indietro
   * - Footer nasconde il bottone Indietro
   */
  isSeeded: boolean;
};

const EMPTY: AutoConnectorState = {
  sessionId: null,
  currentPhase: 0,
  role: null,
  supplierName: "",
  techEmail: "",
  hasAgreement: null,
  uploadMode: "url",
  fileName: null,
  docUrl: "",
  crawlDepth: 2,
  status: null,
  questionnaireDraft: {},
  submittedAt: null,
  isSeeded: false,
};

const slice = createSlice({
  name: "autoConnector",
  initialState: EMPTY,
  reducers: {
    setPhase(state, action: PayloadAction<number>) {
      state.currentPhase = action.payload;
    },
    setRole(state, action: PayloadAction<AutoConnectorRole | null>) {
      state.role = action.payload;
    },
    setSupplierName(state, action: PayloadAction<string>) {
      state.supplierName = action.payload;
    },
    setTechEmail(state, action: PayloadAction<string>) {
      state.techEmail = action.payload;
    },
    setHasAgreement(state, action: PayloadAction<"yes" | "no" | null>) {
      state.hasAgreement = action.payload;
    },
    setUploadMode(state, action: PayloadAction<"file" | "url">) {
      state.uploadMode = action.payload;
    },
    setFileName(state, action: PayloadAction<string | null>) {
      state.fileName = action.payload;
    },
    setDocUrl(state, action: PayloadAction<string>) {
      state.docUrl = action.payload;
    },
    setCrawlDepth(state, action: PayloadAction<number>) {
      state.crawlDepth = action.payload;
    },
    setSessionId(state, action: PayloadAction<string | null>) {
      state.sessionId = action.payload;
    },
    setStatus(state, action: PayloadAction<string | null>) {
      state.status = action.payload;
    },
    setAnswer(state, action: PayloadAction<{ key: string; answer: string }>) {
      state.questionnaireDraft[action.payload.key] = action.payload.answer;
    },
    setSubmittedAt(state, action: PayloadAction<number | null>) {
      state.submittedAt = action.payload;
    },
    reset() {
      return EMPTY;
    },
    resumeSession(_state, action: PayloadAction<{ sessionId: string; supplierName: string; currentPhase: number }>) {
      // Riprende una sessione esistente dalla "Sessioni in corso": setta il
      // sessionId reale + porta l'utente al phase corrispondente allo status pipeline.
      // NON è isSeeded: l'utente può navigare normalmente, è una sessione vera.
      return {
        ...EMPTY,
        sessionId: action.payload.sessionId,
        supplierName: action.payload.supplierName,
        currentPhase: action.payload.currentPhase,
        role: "ota",
        isSeeded: false,
      };
    },
    seedFromIntegrated(_state, action: PayloadAction<{ supplierName: string; role: AutoConnectorRole }>) {
      // Shortcut narrativo: utente clicca un supplier già integrato dalla strip
      // → salta direttamente all'ultimo step "Pronto" (choice) con dati pre-popolati.
      // PHASES = [role(0), intake(1), docs(2), analysis(3), questionnaire(4), loading(5), choice(6)]
      return {
        ...EMPTY,
        supplierName: action.payload.supplierName,
        role: action.payload.role,
        currentPhase: 6, // Step "Pronto" (choice) — l'ultimo
        sessionId: `seed-${action.payload.supplierName.toLowerCase()}`,
        submittedAt: Date.now(),
        isSeeded: true,
      };
    },
  },
});

export const {
  setPhase,
  setRole,
  setSupplierName,
  setTechEmail,
  setHasAgreement,
  setUploadMode,
  setFileName,
  setDocUrl,
  setCrawlDepth,
  setSessionId,
  setStatus,
  setAnswer,
  setSubmittedAt,
  reset,
  seedFromIntegrated,
  resumeSession,
} = slice.actions;

export default slice.reducer;
